import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { formatIqd, fromMillidinars, type BillRowDto } from '@gc/shared';

import { api, ApiError } from '@/lib/api';
import { serviceChargeSign } from '../tabs/checkoutPreview';
import { discountFieldValue, planBillEdit } from './editBillForm';

interface EditBillDialogProps {
  bill: BillRowDto;
  currencySymbol: string;
  onClose: () => void;
  onSaved: () => void;
}

interface FieldError {
  field: string;
  message: string;
}

/**
 * Edit a bill (SPEC §5.6 — `bills.edit`, audited).
 *
 * Only the discount and who the bill belongs to are editable. The subtotal is
 * what the session or order actually generated against a rate snapshot (§4.1);
 * exposing it here would let the till be rewritten after the fact, and no audit
 * entry can undo that.
 *
 * A reason is mandatory, and the server records it in the AuditLog alongside
 * the before/after values.
 *
 * The discount moves only while the bill is OPEN. A paid bill's payments and
 * box entry cannot follow a new discount, so the server refuses one
 * (`bill_settled`) and the box is shown locked here, with the reason, rather
 * than letting the cashier type a figure only to be turned away.
 */
export function EditBillDialog({ bill, currencySymbol, onClose, onSaved }: EditBillDialogProps) {
  const { t, i18n } = useTranslation();

  // The discount shows EXACTLY as settled (250.074 and all) and is sent only
  // when edited — see `editBillForm.ts`. A new one is whole IQD, which is what
  // the API accepts; the conversion to millidinars happens server-side.
  const [discount, setDiscount] = useState(() => discountFieldValue(bill.discountMd));
  const [visitorName, setVisitorName] = useState(bill.visitorName ?? '');
  const [reason, setReason] = useState('');
  const [errors, setErrors] = useState<FieldError[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const discountLocked = bill.status !== 'OPEN';

  const plan = planBillEdit({
    discountMd: bill.discountMd,
    discountEntered: discount,
    visitorName: bill.visitorName,
    visitorEntered: visitorName,
    // Only offered for walk-ins: reassigning a registered account is a
    // customer-records action, not a bill correction.
    canRenameVisitor: !bill.customerAccountId,
    reason,
  });

  const errorFor = (field: string) =>
    errors.find((error) => error.field === field)?.message ??
    (field === 'discountIqd' && plan.kind === 'invalidDiscount'
      ? t('bills.editForm.wholeIqd', { symbol: currencySymbol })
      : undefined);

  const money = (millidinars: string) =>
    formatIqd(fromMillidinars(millidinars), { currencySymbol, locale: i18n.language });

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (plan.kind !== 'ready') return;
    setErrors([]);
    setSubmitting(true);

    try {
      await api.patch(`/bills/${bill.id}`, plan.payload);
      onSaved();
    } catch (caught) {
      // The bill moved between opening this dialog and saving it — usually a
      // checkout at the till. What was typed was judged against the old bill,
      // so the server refused it rather than write it over the new one.
      if (caught instanceof ApiError && caught.code === 'bill_changed') {
        setErrors([{ field: '(root)', message: t('bills.editForm.changedMeanwhile') }]);
      } else if (caught instanceof ApiError && Array.isArray(caught.details)) {
        setErrors(caught.details as FieldError[]);
      } else {
        setErrors([
          {
            field: '(root)',
            message: caught instanceof Error ? caught.message : 'Could not save the bill.',
          },
        ]);
      }
    } finally {
      setSubmitting(false);
    }
  }

  const inputClass =
    'w-full rounded-control border border-subtle bg-input px-3 py-2 text-content outline-none focus:border-primary';

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4">
      <form
        onSubmit={(event) => void handleSubmit(event)}
        className="w-full max-w-md rounded-card border border-subtle bg-raised p-5 shadow-2xl"
      >
        <h2 className="text-lg font-semibold text-content">{t('bills.editTitle')}</h2>
        <p className="mt-1 text-sm text-content-muted">
          {t('bills.editSubtitle', { invoice: bill.invoiceNumber })}
        </p>

        <dl className="mt-4 space-y-1 rounded-control border border-subtle bg-input p-3 text-sm">
          <div className="flex justify-between">
            <dt className="text-content-secondary">{t('bills.columns.price')}</dt>
            <dd className="tabular text-content">{money(bill.subtotalMd)}</dd>
          </div>
          {/* The service charge the bill was settled with. The edit keeps it —
              a new discount moves the total by the difference and nothing
              else — so the cashier sees why a discount of 0 does not leave the
              price. */}
          {bill.serviceChargeMd && bill.serviceChargeMd !== '0' ? (
            <div className="flex justify-between" data-service-charge>
              <dt className="text-content-secondary">
                {t('serviceCharge.line', { percent: bill.serviceChargePercent })}
              </dt>
              <dd className="tabular text-content">
                {serviceChargeSign(bill.serviceChargeMd)}
                {money(bill.serviceChargeMd)}
              </dd>
            </div>
          ) : null}
          <div className="flex justify-between">
            <dt className="text-content-secondary">{t('common.total')}</dt>
            <dd className="tabular text-content">{money(bill.totalMd)}</dd>
          </div>
        </dl>

        <div className="mt-4 space-y-3">
          <label className="block">
            <span className="mb-1 block text-sm text-content-secondary">
              {t('bills.discountIqd', { symbol: currencySymbol })}
            </span>
            {/* step="any": a settled discount like 250.074 must be a value
                the browser will submit. A NEW discount is held to whole IQD
                by `planBillEdit`, with its own message below. */}
            <input
              type="number"
              min="0"
              step="any"
              value={discount}
              onChange={(event) => setDiscount(event.target.value)}
              readOnly={discountLocked}
              aria-describedby={discountLocked ? 'bill-discount-settled' : undefined}
              className={`${inputClass} tabular ${discountLocked ? 'opacity-60' : ''}`}
            />
            {discountLocked ? (
              <p id="bill-discount-settled" className="mt-1 text-xs text-content-muted">
                {t('bills.editForm.discountSettled')}
              </p>
            ) : null}
            {errorFor('discountIqd') ? (
              <p className="mt-1 text-xs text-danger">{errorFor('discountIqd')}</p>
            ) : null}
          </label>

          {bill.customerAccountId ? (
            <p className="text-xs text-content-muted">
              {t('bills.assignedToCustomer', { name: bill.customerName ?? '' })}
            </p>
          ) : (
            <label className="block">
              <span className="mb-1 block text-sm text-content-secondary">
                {t('bills.columns.visitor')}
              </span>
              <input
                value={visitorName}
                onChange={(event) => setVisitorName(event.target.value)}
                className={inputClass}
              />
              {errorFor('visitorName') ? (
                <p className="mt-1 text-xs text-danger">{errorFor('visitorName')}</p>
              ) : null}
            </label>
          )}

          <label className="block">
            <span className="mb-1 block text-sm text-content-secondary">{t('bills.reason')}</span>
            <input
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              required
              maxLength={300}
              placeholder={t('bills.reasonPlaceholder')}
              className={inputClass}
            />
            {errorFor('reason') ? (
              <p className="mt-1 text-xs text-danger">{errorFor('reason')}</p>
            ) : null}
          </label>
        </div>

        {plan.kind === 'nothingChanged' && reason.trim() ? (
          <p className="mt-3 text-xs text-content-muted" data-nothing-changed>
            {t('bills.editForm.nothingChanged')}
          </p>
        ) : null}

        <p className="mt-3 text-xs text-content-muted">{t('bills.auditNotice')}</p>

        {errorFor('(root)') ? (
          <p role="alert" className="mt-3 text-sm text-danger">
            {errorFor('(root)')}
          </p>
        ) : null}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-subtle px-4 py-2 text-sm text-content-secondary hover:bg-hover"
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            disabled={submitting || !reason.trim() || plan.kind !== 'ready'}
            className="rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-fg hover:bg-primary-hover disabled:opacity-60"
          >
            {t('common.save')}
          </button>
        </div>
      </form>
    </div>
  );
}
